import { CapacitorPassToWallet } from '@belongnet/capacitor-pass-to-wallet';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ProfilesApi,
  type WalletCardStatusDto,
} from '@up2stay/up2promo-api-client';
import { useCallback, useState } from 'react';

import { useAuthStore } from '@/core/store/auth.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

export type WalletCardState =
  | 'unknown'
  | 'notInstalled'
  | 'outdated'
  | 'current';

/**
 * Apple delivers the registration seconds after the user taps "Add", so the
 * status is polled for a while once the Wallet sheet has been shown.
 */
const WATCH_INTERVAL_MS = 3_000;
const WATCH_DURATION_MS = 90_000;

async function readDeviceWalletState(
  passTypeIdentifier: string,
  serialNumber: string,
): Promise<boolean | null> {
  if (!isCapacitorNative()) {
    return null;
  }
  try {
    const { passExists } =
      await CapacitorPassToWallet._experimental_passExistsById({
        passTypeIdentifier,
        serialNumber,
      });
    return passExists;
  } catch {
    // No pass-type entitlement (or an older build): fall back to the server's view.
    return null;
  }
}

/**
 * Whether the profile's Wallet Card is installed and current. The server can
 * only see registrations, so on iOS the device's own answer wins for this
 * device — see docs/adr/0010-wallet-card-updates-via-passkit-web-service.md.
 */
export function useWalletCard(profileId: string | null | undefined) {
  const tokens = useAuthStore((state) => state.tokens);
  const queryClient = useQueryClient();
  const [watchUntil, setWatchUntil] = useState<number | null>(null);

  const enabled = Boolean(profileId && tokens?.accessToken);

  const statusQuery = useQuery({
    queryKey: ['wallet-card', profileId],
    queryFn: async (): Promise<WalletCardStatusDto | null> => {
      if (!profileId) {
        return null;
      }
      const response = await ProfilesApi.getWalletCardStatus({
        path: { id: profileId },
      });
      return response.data ?? null;
    },
    enabled,
    staleTime: 15_000,
    refetchInterval: () =>
      watchUntil && Date.now() < watchUntil ? WATCH_INTERVAL_MS : false,
  });

  const status = statusQuery.data ?? null;
  const passTypeIdentifier = status?.passTypeIdentifier ?? null;

  const deviceQuery = useQuery({
    queryKey: ['wallet-card-device', passTypeIdentifier, profileId],
    queryFn: async () =>
      readDeviceWalletState(passTypeIdentifier as string, profileId as string),
    enabled: Boolean(passTypeIdentifier && profileId && isCapacitorNative()),
    staleTime: 15_000,
  });

  const deviceHasPass = deviceQuery.data ?? null;
  const installed = deviceHasPass ?? status?.installed ?? false;
  const outdated = Boolean(status?.installed) && status?.upToDate === false;

  let state: WalletCardState = 'unknown';
  if (status) {
    state = !installed ? 'notInstalled' : outdated ? 'outdated' : 'current';
  }

  if (watchUntil && state === 'current') {
    setWatchUntil(null);
  }

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['wallet-card', profileId],
    });
    void queryClient.invalidateQueries({ queryKey: ['wallet-card-device'] });
  }, [queryClient, profileId]);

  const watchForChange = useCallback(() => {
    setWatchUntil(Date.now() + WATCH_DURATION_MS);
  }, []);

  return {
    state,
    status,
    isLoading: statusQuery.isLoading,
    refresh,
    watchForChange,
  };
}
